<!-- src/components/pettyCash/PettyCashCashlessTable.vue -->
<template>
  <div class="petty-cash-table-root">
    <!-- =====================================================
         REPORT BUTTON
         AVAILABLE TO ALL CASHLESS PETTY CASH USERS
         ===================================================== -->
    <div class="core-actions core-actions--right petty-cash-table-actions">
      <button
        type="button"
        class="btn purple darken-2 waves-effect waves-light"
        :disabled="reportBusy || reportDisabled"
        @click="$emit('print-report')"
      >
        <i class="material-icons left"> picture_as_pdf </i>

        {{ reportBusy ? "Generating report..." : "PRINT PETTY CASH REPORT" }}
      </button>
    </div>

    <!-- =====================================================
         EMPTY
         ===================================================== -->
    <p v-if="rows.length === 0" class="grey-text">
      No petty cash entries recorded for this week yet.
    </p>

    <!-- =====================================================
         TABLE
         ===================================================== -->
    <div v-else class="table-wrap">
      <table class="highlight responsive-table petty-cash-table">
        <thead>
          <tr>
            <!-- 1 -->
            <th>Description</th>

            <!-- 2 -->
            <th class="right-align">Amount</th>

            <!-- 3 -->
            <th>Category</th>

            <!-- 4 -->
            <th>Expense date</th>

            <!-- 5 -->
            <th>Entered By</th>

            <!-- 6 - OPERATIONS ONLY -->
            <th v-if="isOperations" class="center-align">Approval</th>

            <!-- 7 -->
            <th class="center-align">Settled / reimbursed</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="e in rows"
            :key="e._rowKey || e.id"
            :class="{
              'grey lighten-4': isSettled(e),
            }"
          >
            <!-- =================================================
                 DESCRIPTION
                 ================================================= -->
            <td>
              {{ e.description }}

              <div v-if="e.isLate" class="grey-text text-darken-1 petty-cash-note">
                Late receipt
              </div>

              <div v-if="e.isOutstanding" class="grey-text text-darken-1 petty-cash-note">
                Previous unsettled expense
              </div>
            </td>

            <!-- =================================================
                 AMOUNT
                 ================================================= -->
            <td class="right-align">£{{ money2dp(e.amount) }}</td>

            <!-- =================================================
                 CATEGORY
                 ================================================= -->
            <td>
              {{ e.category }}
            </td>

            <!-- =================================================
                 EXPENSE DATE
                 ================================================= -->
            <td>
              {{ formatDateOnly(e.expense_date) }}
            </td>

            <!-- =================================================
                 ENTERED BY
                 ================================================= -->
            <td>
              {{ enteredBy(e) }}
            </td>

            <!-- =================================================
                 APPROVAL
                 OPERATIONS ONLY
                 ================================================= -->
            <td v-if="isOperations" class="center-align">
              <label
                :title="
                  isApproved(e) ? 'Approved for claim' : 'Approve this item for claim'
                "
              >
                <input
                  type="checkbox"
                  :checked="isApproved(e)"
                  :disabled="approvalBusyId === String(e.id) || isSettled(e)"
                  @change="onApprovalToggle(e, $event)"
                />

                <span></span>
              </label>
            </td>

            <!-- =================================================
                 SETTLED / REIMBURSED
                 ================================================= -->
            <td class="center-align">
              <label
                :title="
                  isSettled(e)
                    ? 'Expense has been reimbursed'
                    : 'Tick once reimbursement has been received'
                "
              >
                <input
                  type="checkbox"
                  :checked="isSettled(e)"
                  :disabled="isSettled(e)"
                  @change="onSettledToggle(e)"
                />

                <span>
                  {{ isSettled(e) ? "Settled" : "" }}
                </span>
              </label>
            </td>
          </tr>
        </tbody>

        <!-- =================================================
             TOTAL
             ================================================= -->
        <tfoot>
          <tr>
            <th>Awaiting reimbursement</th>

            <th class="right-align">£{{ money2dp(outstandingTotal) }}</th>

            <th :colspan="isOperations ? 5 : 4"></th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

import { moneyGBP } from "@/scripts/sharedHelpers/formatter";

/* =========================================================
   PROPS
   ========================================================= */

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },

  isOperations: {
    type: Boolean,
    default: false,
  },

  approvalBusyId: {
    type: String,
    default: "",
  },

  reportBusy: {
    type: Boolean,
    default: false,
  },

  reportDisabled: {
    type: Boolean,
    default: false,
  },
});

/* =========================================================
   EMITS
   ========================================================= */

const emit = defineEmits(["toggle-claimed", "toggle-approval", "print-report"]);

/* =========================================================
   FORMATTERS
   ========================================================= */

function money2dp(value) {
  return moneyGBP(value, 2).replace("£", "").trim();
}

/* =========================================================
   DATES
   ========================================================= */

function parseISODateOnly(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return null;
  }

  const iso = raw.slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return null;
  }

  const [year, month, day] = iso.split("-").map(Number);

  return new Date(year, month - 1, day);
}

function formatDateOnly(value) {
  const date = parseISODateOnly(value);

  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

/* =========================================================
   BOOL
   ========================================================= */

function toBool(value) {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }

  return Boolean(value);
}

/* =========================================================
   ENTERED BY
   ========================================================= */

function enteredBy(row) {
  return (
    String(
      row?.created_by_email || row?.receipt_uploaded_by_email || row?.createdByEmail || ""
    ).trim() || "—"
  );
}

/* =========================================================
   APPROVAL
   ========================================================= */

function isApproved(row) {
  return toBool(row?.is_approved);
}

function onApprovalToggle(row, event) {
  if (!props.isOperations) {
    return;
  }

  const approved = Boolean(event?.target?.checked);

  emit("toggle-approval", {
    ...row,
    is_approved: approved,
  });
}

/* =========================================================
   SETTLED
   ========================================================= */

function isSettled(row) {
  return toBool(row?.is_claimed_back) || toBool(row?.is_calimed_back);
}

function onSettledToggle(row) {
  if (isSettled(row)) {
    return;
  }

  emit("toggle-claimed", {
    ...row,

    is_claimed_back: true,

    is_calimed_back: true,
  });
}

/* =========================================================
   TOTAL
   ========================================================= */

const outstandingTotal = computed(() => {
  return (props.rows || []).reduce((sum, row) => {
    if (isSettled(row)) {
      return sum;
    }

    return sum + Number(row?.amount || 0);
  }, 0);
});
</script>

<style scoped>
.petty-cash-table-root {
  width: 100%;
}

.petty-cash-table-actions {
  margin-bottom: 14px;
}

.petty-cash-table {
  min-width: 1050px;
}

.petty-cash-note {
  font-size: 12px;
}

.petty-cash-table label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
